// src/Product/customers.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import "./customers.css";
import CustomerStats from "../Product/CustomerStats";
import AddCustomerModal from "./AddCustomerModal";
import { useCompany } from "../context/CompanyContext";
import {
  Search,
  PlusCircle,
  Trash2,
  CalendarDays,
  RefreshCcw,
  Eye,
  Pencil,
  X,
  Save,
  Users,
  Wallet,
  ReceiptText,
  RotateCcw,
  AlertCircle,
} from "lucide-react";
import CustomerMiniReportModal from "../Product/CustomerMiniReportModal";

function money(n) {
  const v = Number(n || 0);
  return `₦${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function safeLower(x) {
  return String(x || "")
    .trim()
    .toLowerCase();
}

function EditCustomerModal({ open, onClose, onSaved, companyId, customer }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    name: "",
    company_name: "",
    address: "",
    phone: "",
  });

  useEffect(() => {
    if (!open) return;

    setErr("");
    setBusy(false);

    setForm({
      name: customer?.customer_name || "",
      company_name:
        customer?.company_name && customer.company_name !== "—"
          ? customer.company_name
          : "",
      address: customer?.address || "",
      phone: customer?.phone_number || "",
    });
  }, [open, customer]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function save() {
    setErr("");

    if (!companyId) return setErr("Missing company.");
    if (!customer?.customer_id) return setErr("Missing customer id.");
    if (!String(form.name || "").trim()) {
      return setErr("Customer name is required.");
    }

    setBusy(true);

    try {
      const payload = {
        name: String(form.name || "").trim(),
        company_name: String(form.company_name || "").trim() || null,
        address: String(form.address || "").trim() || null,
        phone: String(form.phone || "").trim() || null,
      };

      const { error } = await supabase
        .from("customers")
        .update(payload)
        .eq("company_id", companyId)
        .eq("id", customer.customer_id);

      if (error) throw error;

      onSaved?.();
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Failed to update customer.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="kp-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="kp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kp-modal-head">
          <div>
            <p className="kp-eyebrow">Customer Details</p>
            <h2>Edit Customer</h2>
            <p>Update the customer’s basic profile information.</p>
          </div>

          <button className="kp-icon-btn" onClick={onClose} disabled={busy}>
            <X size={18} />
          </button>
        </div>

        <div className="kp-modal-form">
          <label>
            Customer Name
            <input
              className="kp-input"
              placeholder="Customer name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              disabled={busy}
            />
          </label>

          <label>
            Company Name
            <input
              className="kp-input"
              placeholder="Company name optional"
              value={form.company_name}
              onChange={(e) => setField("company_name", e.target.value)}
              disabled={busy}
            />
          </label>

          <label>
            Address
            <input
              className="kp-input"
              placeholder="Address optional"
              value={form.address}
              onChange={(e) => setField("address", e.target.value)}
              disabled={busy}
            />
          </label>

          <label>
            Phone Number
            <input
              className="kp-input"
              placeholder="Phone number optional"
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
              disabled={busy}
            />
          </label>

          {err && <div className="kp-banner error">{err}</div>}
        </div>

        <div className="kp-modal-actions">
          <button className="kp-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="kp-btn primary" onClick={save} disabled={busy}>
            <Save size={16} />
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, hint, tone = "default" }) {
  return (
    <div className={`cust-summary-card ${tone}`}>
      <div className="cust-summary-icon">
        <Icon size={19} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {hint && <small>{hint}</small>}
      </div>
    </div>
  );
}

export default function Customers() {
  const { companyId } = useCompany();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [miniOpen, setMiniOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);

  const fetchCustomerSummary = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setErrorMessage("");

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setErrorMessage("Not authenticated.");
        setBusy(false);
        return;
      }

      let baseList = [];

      const rpcRes = await supabase.rpc("get_customer_trade_summary", {
        p_company_id: companyId,
        p_from: fromDate || null,
        p_to: toDate || null,
      });

      if (rpcRes?.error) {
        console.warn("get_customer_trade_summary error:", rpcRes.error);
      } else {
        baseList = rpcRes?.data || [];
      }

      const customerIdsFromRpc = (baseList || [])
        .map((r) => r.customer_id)
        .filter(Boolean);

      let custQ = supabase
        .from("customers")
        .select("id,name,company_name,address,phone,open_balance")
        .eq("company_id", companyId)
        .order("name", { ascending: true });

      if (customerIdsFromRpc.length) {
        custQ = custQ.in("id", customerIdsFromRpc);
      }

      const { data: customers, error: custErr } = await custQ;
      if (custErr) throw custErr;

      const customersList = customers || [];
      const customerIds = customersList.map((c) => c.id);

      if (!customerIds.length) {
        setRows([]);
        setFilteredRows([]);
        return;
      }

      let invQ = supabase
        .from("invoices")
        .select("id, customer_id, total, status, date")
        .eq("company_id", companyId)
        .in("customer_id", customerIds);

      if (fromDate) invQ = invQ.gte("date", fromDate);
      if (toDate) invQ = invQ.lte("date", toDate);

      const { data: invoices, error: invErr } = await invQ;
      if (invErr) throw invErr;

      const invList = invoices || [];
      const invIds = invList.map((i) => i.id);

      let paysByInv = {};
      if (invIds.length) {
        const { data: pays, error: payErr } = await supabase
          .from("invoice_payments")
          .select("invoice_id,type,amount")
          .eq("company_id", companyId)
          .in("invoice_id", invIds);

        if (payErr) throw payErr;

        (pays || []).forEach((p) => {
          (paysByInv[p.invoice_id] ||= []).push(p);
        });
      }

      let saleQ = supabase
        .from("sales_transactions")
        .select("id, customer_id, total, payment_status, sale_date")
        .eq("company_id", companyId)
        .in("customer_id", customerIds);

      if (fromDate) saleQ = saleQ.gte("sale_date", fromDate);
      if (toDate) saleQ = saleQ.lte("sale_date", toDate);

      const { data: sales, error: salesErr } = await saleQ;
      if (salesErr) throw salesErr;

      const saleList = sales || [];
      const saleIds = saleList.map((s) => s.id);

      let setsBySale = {};
      if (saleIds.length) {
        const { data: sets, error: setErr } = await supabase
          .from("sale_settlements")
          .select("sale_id,type,amount")
          .eq("company_id", companyId)
          .in("sale_id", saleIds);

        if (setErr) throw setErr;

        (sets || []).forEach((s) => {
          (setsBySale[s.sale_id] ||= []).push(s);
        });
      }

      const aggByCustomer = new Map();

      customersList.forEach((c) => {
        aggByCustomer.set(c.id, {
          invoiced_total: 0,
          paid_total: 0,
          refunded_total: 0,
          balance_due: Number(c.open_balance || 0),
        });
      });

      invList.forEach((inv) => {
        const cid = inv.customer_id;
        if (!aggByCustomer.has(cid)) return;

        const total = Number(inv.total || 0);
        const status = String(inv.status || "open").toLowerCase();
        const pays = paysByInv[inv.id] || [];

        const paidFromPayments = pays
          .filter((p) => String(p.type || "").toUpperCase() === "PAYMENT")
          .reduce((s, p) => s + Number(p.amount || 0), 0);

        const refunded = pays
          .filter((p) => String(p.type || "").toUpperCase() === "REFUND")
          .reduce((s, p) => s + Number(p.amount || 0), 0);

        const paidEffective = status === "paid" ? total : paidFromPayments;
        const balanceEffective =
          status === "paid" ? 0 : total - (paidFromPayments - refunded);

        const a = aggByCustomer.get(cid);
        a.invoiced_total += total;
        a.paid_total += paidEffective;
        a.refunded_total += refunded;
        a.balance_due += balanceEffective;
      });

      saleList.forEach((sale) => {
        const cid = sale.customer_id;
        if (!aggByCustomer.has(cid)) return;

        const total = Number(sale.total || 0);
        const paymentStatus = String(
          sale.payment_status || "UNPAID"
        ).toUpperCase();

        const sets = setsBySale[sale.id] || [];

        const paidFromSets = sets
          .filter((p) => String(p.type || "").toUpperCase() === "PAYMENT")
          .reduce((s, p) => s + Number(p.amount || 0), 0);

        const refunded = sets
          .filter((p) => String(p.type || "").toUpperCase() === "REFUND")
          .reduce((s, p) => s + Number(p.amount || 0), 0);

        const basePaid = paymentStatus === "PAID" ? total : 0;
        const paid = basePaid + paidFromSets;
        const balance = total - (paid - refunded);

        const a = aggByCustomer.get(cid);
        a.invoiced_total += total;
        a.paid_total += paid;
        a.refunded_total += refunded;
        a.balance_due += balance;
      });

      const rpcById = new Map((baseList || []).map((r) => [r.customer_id, r]));

      const finalRows = customersList.map((c) => {
        const rpc = rpcById.get(c.id) || {};
        const a = aggByCustomer.get(c.id) || {
          invoiced_total: 0,
          paid_total: 0,
          refunded_total: 0,
          balance_due: 0,
        };

        return {
          customer_id: c.id,
          customer_name: rpc.customer_name || c.name || "—",
          company_name: rpc.company_name || c.company_name || "—",
          address: c.address || null,
          phone_number: c.phone || null,
          invoiced_total: a.invoiced_total,
          paid_total: a.paid_total,
          refunded_total: a.refunded_total,
          balance_due: a.balance_due,
        };
      });

      setRows(finalRows);
      setFilteredRows(finalRows);
    } catch (e) {
      console.error(e);
      setErrorMessage(e?.message || "Failed to load customer summary.");
      setRows([]);
      setFilteredRows([]);
    } finally {
      setBusy(false);
    }
  }, [companyId, fromDate, toDate]);

  useEffect(() => {
    fetchCustomerSummary();
  }, [fetchCustomerSummary]);

  useEffect(() => {
    const q = safeLower(searchQuery);

    if (!q) {
      setFilteredRows(rows);
      return;
    }

    setFilteredRows(
      (rows || []).filter((r) => {
        const name = safeLower(r.customer_name);
        const company = safeLower(r.company_name);
        const address = safeLower(r.address);
        const phone = safeLower(r.phone_number);

        return (
          name.includes(q) ||
          company.includes(q) ||
          address.includes(q) ||
          phone.includes(q)
        );
      })
    );
  }, [searchQuery, rows]);

  const totals = useMemo(() => {
    const list = filteredRows || [];
    const invoiced = list.reduce(
      (s, r) => s + Number(r.invoiced_total || 0),
      0
    );
    const paid = list.reduce((s, r) => s + Number(r.paid_total || 0), 0);
    const refunded = list.reduce(
      (s, r) => s + Number(r.refunded_total || 0),
      0
    );
    const balance = list.reduce((s, r) => s + Number(r.balance_due || 0), 0);

    return {
      count: list.length,
      invoiced,
      paid,
      refunded,
      balance,
      collectionRate: invoiced > 0 ? (paid / invoiced) * 100 : 0,
    };
  }, [filteredRows]);

  const handleDeleteCustomer = async (customerId) => {
    if (
      !window.confirm(
        "Delete this customer? This cannot be undone if there are no database restrictions preventing it."
      )
    ) {
      return;
    }

    const { error } = await supabase
      .from("customers")
      .delete()
      .eq("id", customerId)
      .eq("company_id", companyId);

    if (error) {
      console.error("Error deleting customer:", error);
      alert("Could not delete customer.");
    } else {
      await fetchCustomerSummary();
      alert("Customer deleted.");
    }
  };

  function openMiniReport(row) {
    setSelectedCustomer(row);
    setMiniOpen(true);
  }

  function openEdit(row) {
    setEditCustomer(row);
    setEditOpen(true);
  }

  function clearFilters() {
    setSearchQuery("");
    setFromDate("");
    setToDate("");
  }

  return (
    <div className="customers-container kp-customers-page">
      <CustomerStats />

      <div className="kp-customers-hero">
        <div>
          <p className="kp-eyebrow">Customers</p>
          <h1>Customer Receivables</h1>
          <p>
            Manage customers, review sales activity, track collections, and
            follow up on outstanding balances.
          </p>
        </div>

        <button
          className="kp-btn primary"
          onClick={() => setShowModal(true)}
          disabled={busy}
        >
          <PlusCircle size={17} />
          Add New Customer
        </button>
      </div>

      {errorMessage && (
        <div className="kp-banner error">
          <AlertCircle size={16} />
          {errorMessage}
        </div>
      )}

      <section className="cust-summary-grid">
        <SummaryCard
          icon={Users}
          label="Customers"
          value={totals.count}
          hint="Based on current filters"
        />
        <SummaryCard
          icon={ReceiptText}
          label="Total Sales"
          value={money(totals.invoiced)}
          hint="Invoices and quick sales"
        />
        <SummaryCard
          icon={Wallet}
          label="Amount Received"
          value={money(totals.paid)}
          hint={`${totals.collectionRate.toFixed(1)}% collection rate`}
          tone="success"
        />
        <SummaryCard
          icon={RotateCcw}
          label="Refunds"
          value={money(totals.refunded)}
          hint="Refunds issued"
          tone="warning"
        />
        <SummaryCard
          icon={AlertCircle}
          label="Balance Due"
          value={money(totals.balance)}
          hint={
            totals.balance > 0 ? "Outstanding receivables" : "No balance due"
          }
          tone={totals.balance > 0 ? "danger" : "success"}
        />
      </section>

      <section className="kp-panel cust-filter-panel">
        <div className="cust-filter-top">
          <div>
            <h3>Customer List</h3>
            <p>
              Search, filter by period, view customer reports, or update
              details.
            </p>
          </div>

          {busy && <span className="kp-loading-pill">Loading…</span>}
        </div>

        <div className="cust-filter-grid">
          <div className="kp-search-wrap">
            <Search size={17} />
            <input
              type="text"
              placeholder="Search customer, company, phone or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="cust-period-wrap">
            <span className="period-label">
              <CalendarDays size={15} />
              Period
            </span>

            <input
              className="kp-input"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              disabled={busy}
              title="From date"
            />

            <input
              className="kp-input"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              disabled={busy}
              title="To date"
            />
          </div>

          <div className="cust-filter-actions">
            <button
              className="kp-btn ghost"
              onClick={fetchCustomerSummary}
              disabled={busy}
            >
              <RefreshCcw size={16} />
              Refresh
            </button>

            <button
              className="kp-btn light"
              onClick={clearFilters}
              disabled={busy}
            >
              Clear
            </button>
          </div>
        </div>
      </section>

      <section className="kp-panel cust-table-panel">
        <div className="cust-table-head">
          <div>
            <h3>Customers</h3>
            <p>
              Showing <strong>{filteredRows.length}</strong> customer(s)
            </p>
          </div>
        </div>

        <div className="cust-table-wrap">
          <table className="customer-table modern">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contact</th>
                <th className="num">Sales</th>
                <th className="num">Received</th>
                <th className="num">Refunds</th>
                <th className="num">Balance Due</th>
                <th className="center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.customer_id}>
                  <td>
                    <div className="cust-name-block">
                      <strong>{row.customer_name || "—"}</strong>
                      <span>{row.company_name || "No company name"}</span>
                    </div>
                  </td>

                  <td>
                    <div className="cust-contact-block">
                      <span>{row.phone_number || "No phone number"}</span>
                      <small>{row.address || "No address saved"}</small>
                    </div>
                  </td>

                  <td className="num">{money(row.invoiced_total)}</td>
                  <td className="num success-text">{money(row.paid_total)}</td>
                  <td className="num warning-text">
                    {money(row.refunded_total)}
                  </td>
                  <td
                    className={`num ${
                      Number(row.balance_due || 0) > 0 ? "danger-text" : ""
                    }`}
                  >
                    <strong>{money(row.balance_due)}</strong>
                  </td>

                  <td className="center">
                    <div className="cust-action-row">
                      <button
                        className="kp-action-btn"
                        title="Mini report"
                        onClick={() => openMiniReport(row)}
                        disabled={busy}
                      >
                        <Eye size={15} />
                        Report
                      </button>

                      <button
                        className="kp-action-btn"
                        title="Edit customer"
                        onClick={() => openEdit(row)}
                        disabled={busy}
                      >
                        <Pencil size={15} />
                        Edit
                      </button>

                      <button
                        className="kp-action-btn"
                        title="View payments"
                        onClick={() =>
                          navigate(`/customer/${row.customer_id}/payments`)
                        }
                        disabled={busy}
                      >
                        Payments
                      </button>

                      <button
                        className="kp-icon-btn danger"
                        title="Delete customer"
                        onClick={() => handleDeleteCustomer(row.customer_id)}
                        disabled={busy}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!filteredRows.length && !busy && (
            <div className="cust-empty-state">
              <div className="cust-empty-icon">
                <Users size={26} />
              </div>
              <h3>No customers found</h3>
              <p>
                Try clearing your filters or add a new customer to begin
                tracking receivables.
              </p>
              <button
                className="kp-btn primary"
                onClick={() => setShowModal(true)}
              >
                <PlusCircle size={16} />
                Add Customer
              </button>
            </div>
          )}
        </div>
      </section>

      {showModal && (
        <AddCustomerModal
          onClose={() => setShowModal(false)}
          onAdd={() => {
            setShowModal(false);
            fetchCustomerSummary();
          }}
        />
      )}

      <CustomerMiniReportModal
        open={miniOpen}
        onClose={() => setMiniOpen(false)}
        companyId={companyId}
        customer={selectedCustomer}
        fromDate={fromDate || ""}
        toDate={toDate || ""}
      />

      <EditCustomerModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={() => fetchCustomerSummary()}
        companyId={companyId}
        customer={editCustomer}
      />
    </div>
  );
}
